/**
 * Confirmación de una acción con consecuencias (ADR-0018): «¿Cerrar CDT-1 como Hecha?» con subtareas
 * abiertas, o «¿Empezar CDT-101 aunque está bloqueada?». Más invasivo que un aviso a propósito: la acción
 * no se envía hasta que la persona elige «Cerrar igual» (o lo que diga `textoConfirmar`).
 *
 * Genérico: título, una explicación corta, el detalle de lo pendiente (`children`, p. ej. una lista de
 * tareas) y la consecuencia. «Cancelar» tiene el foco al abrir (es la opción por defecto) y Esc o un clic
 * en el velo también cancelan. La carcasa (portal, foco atrapado, hoja en el celular) es `Dialogo`.
 */
import { useId, useRef, type ReactNode } from 'react';
import { Dialogo } from './Dialogo';
import { Icono } from './Icono';
import './DialogoConfirmar.css';

interface Props {
  titulo: string;
  /** Explicación corta bajo el título («Lleva 1 de 4 subtareas hechas…»). */
  descripcion?: ReactNode;
  /** Qué queda pendiente (listas de tareas con su título de sección). */
  children?: ReactNode;
  /** Qué pasa si se sigue igual. */
  consecuencia?: ReactNode;
  textoConfirmar: string;
  textoCancelar?: string;
  onConfirmar: () => void;
  onCancelar: () => void;
}

export function DialogoConfirmar({
  titulo,
  descripcion,
  children,
  consecuencia,
  textoConfirmar,
  textoCancelar = 'Cancelar',
  onConfirmar,
  onCancelar,
}: Props) {
  const id = useId();
  const cancelar = useRef<HTMLButtonElement>(null);
  return (
    <Dialogo
      etiquetadoPor={`${id}-titulo`}
      descritoPor={descripcion ? `${id}-descripcion` : undefined}
      focoInicial={cancelar}
      className="confirmar"
      alerta
      onCancelar={onCancelar}
    >
      <div className="confirmar__cab">
        <span className="confirmar__icono" aria-hidden="true">
          <Icono nombre="alerta" tamano={20} />
        </span>
        <div className="confirmar__textos">
          <h2 id={`${id}-titulo`} className="dialogo__titulo">
            {titulo}
          </h2>
          {descripcion && (
            <p id={`${id}-descripcion`} className="dialogo__ayuda">
              {descripcion}
            </p>
          )}
        </div>
      </div>
      {children && <div className="confirmar__cuerpo">{children}</div>}
      {consecuencia && <p className="confirmar__consecuencia">{consecuencia}</p>}
      <div className="dialogo__acciones">
        <button ref={cancelar} type="button" className="boton" onClick={onCancelar}>
          {textoCancelar}
        </button>
        <button type="button" className="boton boton--aviso-lleno" onClick={onConfirmar}>
          {textoConfirmar}
        </button>
      </div>
    </Dialogo>
  );
}
